import React, { useState, useEffect } from 'react';
import { NamedCollection, Slide } from '../types';
import { slideApiService } from '../services/slideApiService';
import { useShop } from '../contexts/ShopContext';
import { collectionCreatorLabel, slidePickerData } from '../utils/collectionPermissions';
import NamePromptModal from './NamePromptModal';

/**
 * Quick "add this slide to collections" picker (slide-svc/QUICK_ADD_TO_COLLECTION_PLAN.md), opened
 * from a slide card's add-to-collection button or the slide view modal. Mirrors the iOS
 * SlideCollectionsPickerSheet.
 *
 * A TOGGLE list: every collection that already contains the slide is checked. Clicking an unchecked
 * row adds the slide, clicking a checked row removes it; each click saves immediately and the modal
 * stays open until Done. "New collection" creates a collection that already contains the slide.
 * Lists only collections the user may edit, loaded for the SLIDE's shop (collections are shop-scoped).
 *
 * (Separate from AddToCollectionModal, which adds a whole favorites set to one collection and closes.)
 *
 * The root stops click/mouse/touch propagation so, wherever it is rendered, interacting with it can
 * never reach a slide card's open-detail or photo-swipe handlers.
 */
interface SlideCollectionsPickerModalProps {
  slide: Slide;
  onClose: () => void;
}

const BORDER = 'var(--border-light)';
const MUTED = 'var(--text-accent)';
const TEXT = 'var(--text-accent)';
const PRIMARY = 'var(--text-accent)';

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

const SlideCollectionsPickerModal: React.FC<SlideCollectionsPickerModalProps> = ({ slide, onClose }) => {
  const { userRole } = useShop();
  const [collections, setCollections] = useState<NamedCollection[]>([]);
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const all = await slideApiService.listCollections(slide.shopId);
        if (cancelled) return;
        const { editable, memberIds: ids } = slidePickerData(all, slide.id, userRole);
        setCollections(editable);
        setMemberIds(ids);
      } catch (err) {
        console.error('Failed to load collections:', err);
        if (!cancelled) setError("Couldn't load your collections.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slide.shopId, slide.id, userRole]);

  // Escape closes (unless the New collection prompt is open; Escape there should only close that).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showCreate) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, showCreate]);

  const replace = (updated: NamedCollection) =>
    setCollections(prev => prev.map(c => (c.id === updated.id ? updated : c)));

  /** Add or remove this slide, saved immediately. On failure nothing changes locally. */
  const toggle = async (c: NamedCollection) => {
    if (busyIds.has(c.id)) return;
    const removing = memberIds.has(c.id);
    setBusyIds(prev => new Set(prev).add(c.id));
    setError(null);
    try {
      const updated = removing
        ? await slideApiService.removeSlideFromCollection(slide.shopId, c.id, slide.id)
        : await slideApiService.addSlidesToCollection(slide.shopId, c.id, [slide.id]);
      replace(updated);
      setMemberIds(prev => {
        const next = new Set(prev);
        if (removing) next.delete(c.id); else next.add(c.id);
        return next;
      });
    } catch (err) {
      console.error('Failed to update collection membership:', err);
      setError(removing
        ? `Couldn't remove from "${c.name}". Please try again.`
        : `Couldn't add to "${c.name}". Please try again.`);
    } finally {
      setBusyIds(prev => { const next = new Set(prev); next.delete(c.id); return next; });
    }
  };

  /** Create a collection that already contains this slide (one call), shown checked at the top. */
  const createWithSlide = async (name: string) => {
    const created = await slideApiService.createCollection(slide.shopId, name, [slide.id]);
    setCollections(prev => [created, ...prev]);
    setMemberIds(prev => new Set(prev).add(created.id));
    setShowCreate(false);
  };

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      onMouseDown={stop} onMouseUp={stop} onMouseMove={stop}
      onTouchStart={stop} onTouchMove={stop} onTouchEnd={stop}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px',
        cursor: 'default',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="slide-collections-picker-title"
        onClick={stop}
        style={{ backgroundColor: 'var(--bg-card)', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '460px', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* No Cancel: every click saves immediately, so there is nothing pending to cancel. The x,
            Escape, the backdrop and Done all simply close. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <h3 id="slide-collections-picker-title" style={{ margin: '0 0 4px', fontSize: '1.125rem', fontWeight: 600, color: TEXT }}>
            Add to Collection
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            title="Close"
            style={{
              background: 'none', border: 'none', fontSize: '24px', lineHeight: 1,
              color: 'var(--text-secondary)', cursor: 'pointer', padding: '0 4px', marginTop: '-4px',
            }}
          >
            ×
          </button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: '0.875rem', color: MUTED }}>
          Click a collection to add this slide. Click a checked one to remove it.
        </p>

        {error && (
          <div style={{ color: 'var(--error-text)', fontSize: '0.8125rem', marginBottom: '12px' }}>{error}</div>
        )}

        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setShowCreate(true)}
          style={{ alignSelf: 'flex-start', marginBottom: '12px' }}
        >
          + New collection
        </button>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: MUTED }}>
              <div className="spinner-sm mx-auto mb-2" />
              Loading…
            </div>
          ) : collections.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: MUTED, fontSize: '0.875rem' }}>
              No collections yet. Create one and this slide goes straight into it.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {collections.map(collection => {
                const checked = memberIds.has(collection.id);
                const busy = busyIds.has(collection.id);
                const creator = collectionCreatorLabel(collection);
                return (
                  <button
                    key={collection.id}
                    role="checkbox"
                    aria-checked={checked}
                    disabled={busy}
                    onClick={() => toggle(collection)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
                      width: '100%', textAlign: 'left', padding: '12px 14px',
                      border: `1px solid ${checked ? PRIMARY : BORDER}`, borderRadius: '8px',
                      backgroundColor: 'var(--bg-card)', cursor: busy ? 'progress' : 'pointer',
                    }}
                  >
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600, color: TEXT, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {collection.name}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: MUTED }}>
                        {collection.slideCount} {collection.slideCount === 1 ? 'slide' : 'slides'}
                        {creator ? ` · by ${creator}` : ''}
                      </span>
                    </span>
                    <span aria-hidden="true" style={{ flexShrink: 0, width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {busy ? (
                        <div className="spinner-sm" />
                      ) : checked ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={PRIMARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <path d="m8 12 3 3 5-6" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={BORDER} strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>

        {showCreate && (
          <NamePromptModal
            title="New Collection"
            confirmLabel="Create"
            onCancel={() => setShowCreate(false)}
            onSubmit={createWithSlide}
            zIndex={1200}
          />
        )}
      </div>
    </div>
  );
};

export default SlideCollectionsPickerModal;
